import { useEffect, useRef, useState } from "react"
import { resolveHasCompareChanges } from "authoring/routes/project/scoped-schedules/lib/resolve-has-compare-changes"
import { getAuthoringScheduleWorkerClient } from "./authoring-schedule-worker-client"

/**
 * Async draft-vs-published compare for gating Compare/Submit actions.
 *
 * Returns `null` while compare data is loading or a compare job is in flight.
 *
 * @param {object} params
 * @param {string} [params.draftId]
 * @param {object | null} params.draft
 * @param {object | null} params.compareSource
 * @param {boolean} params.compareLoading
 * @param {object} [params.macroMap]
 * @param {boolean} [params.enabled]
 * @returns {boolean | null}
 */
export function useAsyncCompareChanges({ draftId, draft, compareSource, compareLoading, macroMap, enabled = true }) {
    const [hasCompareChanges, setHasCompareChanges] = useState(null)
    const latestRequestRef = useRef(0)

    useEffect(runCompareWhenReady, [draftId, draft, compareSource, compareLoading, macroMap, enabled])

    return hasCompareChanges

    function runCompareWhenReady() {
        if (!enabled) {
            setHasCompareChanges(null)
            return
        }

        if (compareLoading || !draft || !compareSource) {
            setHasCompareChanges(null)
            return
        }

        const requestId = ++latestRequestRef.current
        setHasCompareChanges(null)

        const client = draftId ? getAuthoringScheduleWorkerClient({ draftId }) : null
        if (!client) {
            setHasCompareChanges(
                resolveHasCompareChanges({
                    draft,
                    compareSource,
                    compareLoading: false,
                    macroMap,
                })
            )
            return
        }

        const jobId = client.scheduleCompare({
            draft,
            compareSource,
            macroMap,
        })

        client
            .waitForCompare(jobId)
            .then((result) => {
                if (requestId !== latestRequestRef.current) return
                if (result === null) {
                    setHasCompareChanges(
                        resolveHasCompareChanges({
                            draft,
                            compareSource,
                            compareLoading: false,
                            macroMap,
                        })
                    )
                    return
                }
                setHasCompareChanges(result)
            })
            .catch(() => {
                if (requestId !== latestRequestRef.current) return
                setHasCompareChanges(
                    resolveHasCompareChanges({
                        draft,
                        compareSource,
                        compareLoading: false,
                        macroMap,
                    })
                )
            })
    }
}
